import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Files from 'react-files';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Divider, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit';
import { useQueryClient } from '@tanstack/react-query';

import { convertBlobToBase64 } from '@/utils/file';
import { generateUUIDv4 } from '@/utils/number';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import AlertModal from '@/views/components/Modals/AlertModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import {
  useApplyOCClaimRecoveryPercent,
  useGetOCClaimItems,
  useOCClaimScrollPagination,
} from '@/views/screens/Claims/OverCharge/hooks';
import { invalidateOCClaimTabQueries } from '@/views/screens/Claims/OverCharge/hooks/oc-claim-query-keys';

import { ReactComponent as FileIcon } from '../../../../../../../../assets/icons/files-records.svg';
import {
  getOverChargeItemsFromExcelInProgress,
  updateOverChargeClaimInProgress,
} from '../../../../../../../../redux/Claims/OverCharge/OverChargeActions';
import { setAppLoader, setWarningModal } from '../../../../../../../../redux/common/CommonActions';
import { formateAmount } from '../../../../../../../../utilz';
import { overChargeClaimsItemsSectionLocalToAPI } from '../../../../../../../../utilz/OverChargeClaimUtils';
import VerticalLine from '../../../../../../../components/Bars/VerticalLine';
import Button from '../../../../../../../components/Buttons/Button';
import SvgConverter from '../../../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../../../components/Inputs/FormInput';
import PrimaryLabel from '../../../../../../../components/Labels/PrimaryLabel';
import Paper from '../../../../../../../components/Paper/Paper';
import FooterContentCard from '../../../../../../common/FooterContentCard';
import OverChargeClaimEmailDetailsModal from '../OverChargeDetails/OverChargeClaimEmailDetailsModal';
import OverChargeItemsCard from './OverChargeItemsCard';
import OverChargeUploadItemsModal from './OverChargeUploadItemsModal';

const getItemKey = (item) => item.tempId || item.itemID;
const ZERO = 0;
const OVERCHARGE_PERCENT_MAX = 100;
const OverChargeItems = forwardRef(({ claimDetails }, ref) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const itemsFromExcel = useSelector((state) => state.overChargeClaims.itemsFromExcel);
  const fbOcKey = claimDetails?.fbOcKey;
  const {
    data: claimItemsQueryData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useGetOCClaimItems({ fbOcKey });
  const { mutate: applyRecoveryPercent, isPending: isApplyingRecovery } = useApplyOCClaimRecoveryPercent();

  const claimItems = useMemo(
    () => ({
      data: claimItemsQueryData?.items ?? [],
      totalBilledAmount: claimItemsQueryData?.totalBilledAmount ?? ZERO,
      totalCorrectCharges: claimItemsQueryData?.totalCorrectCharges ?? ZERO,
      totalOverchargeAmount: claimItemsQueryData?.totalOverchargeAmount ?? ZERO,
      totalPaidAmount: claimItemsQueryData?.totalPaidAmount ?? ZERO,
      totalRecoveryAmount: claimItemsQueryData?.totalRecoveryAmount ?? ZERO,
      totalRows: claimItemsQueryData?.totalRows ?? ZERO,
    }),
    [claimItemsQueryData],
  );

  const [itemList, setItemList] = useState([]);
  const [emailDetailsModal, setEmailDetailsModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [overchargePercentForm] = Form.useForm();
  const [duplicateClaimText, setDuplicateClaimText] = useState(false);
  const [dirtyItemKeys, setDirtyItemKeys] = useState(new Set());
  const [deletedItems, setDeletedItems] = useState([]);

  const filteredItems = useMemo(() => {
    if (!claimDetails?.fbOcKey || !Array.isArray(claimItems.data)) {
      return [];
    }

    return claimItems.data.filter((item) => Number(item.fbOcKey) === Number(claimDetails.fbOcKey));
  }, [claimDetails?.fbOcKey, claimItems.data]);

  const isClaimItemsForActiveClaim = useMemo(() => {
    if (!claimDetails?.fbOcKey) {
      return false;
    }

    if (!Array.isArray(claimItems.data) || claimItems.data.length === 0) {
      return Number(claimItems.totalRows ?? 0) === 0;
    }

    return claimItems.data.every((item) => Number(item.fbOcKey) === Number(claimDetails.fbOcKey));
  }, [claimDetails?.fbOcKey, claimItems.data, claimItems.totalRows]);

  const itemFormRefs = useRef(new Map());
  const itemListRef = useRef(itemList);
  const dirtyItemKeysRef = useRef(dirtyItemKeys);
  const filteredItemsRef = useRef(filteredItems);
  const deletedItemsRef = useRef(deletedItems);
  const lastClaimKeyRef = useRef(null);

  useEffect(() => {
    itemListRef.current = itemList;
  }, [itemList]);

  useEffect(() => {
    dirtyItemKeysRef.current = dirtyItemKeys;
  }, [dirtyItemKeys]);

  useEffect(() => {
    filteredItemsRef.current = filteredItems;
  }, [filteredItems]);

  useEffect(() => {
    deletedItemsRef.current = deletedItems;
  }, [deletedItems]);

  useEffect(() => {
    const currentFbOcKey = claimDetails?.fbOcKey;
    if (!currentFbOcKey) {
      return;
    }
    if (lastClaimKeyRef.current !== null && lastClaimKeyRef.current !== currentFbOcKey) {
      setItemList([]);
      setDeletedItems([]);
      setDirtyItemKeys(new Set());
      itemFormRefs.current.clear();
    }
    lastClaimKeyRef.current = currentFbOcKey;
  }, [claimDetails?.fbOcKey]);

  const markItemDirty = (itemKey) => {
    setDirtyItemKeys((prev) => new Set(prev).add(itemKey));
  };

  const registerItemForm = (itemKey, formInstance) => {
    if (formInstance) {
      itemFormRefs.current.set(itemKey, formInstance);
    } else {
      itemFormRefs.current.delete(itemKey);
    }
  };

  useImperativeHandle(ref, () => ({
    getItemsData: () => {
      const originalItems = filteredItemsRef.current || [];
      const originalItemKeys = new Set(originalItems.map((item) => item.itemID));

      const activeItems = itemListRef.current.map((item) => {
        const itemKey = getItemKey(item);
        const form = itemFormRefs.current.get(itemKey);
        const formData = form?.getFieldsValue() ?? item;

        let dirtyFlag = 'N';

        const itemID = formData.itemID || item.itemID;
        const existsInOriginal = itemID && itemID !== 0 && originalItemKeys.has(itemID);
        const isDirty = dirtyItemKeysRef.current.has(itemKey);

        if (item.isNew) {
          dirtyFlag = existsInOriginal ? (isDirty ? 'U' : 'N') : 'I';
        } else if (existsInOriginal) {
          dirtyFlag = isDirty ? 'U' : 'N';
        } else if (isDirty) {
          dirtyFlag = 'U';
        }

        return { ...formData, dirtyFlag };
      });

      const deletedItemsData = deletedItemsRef.current.map((item) => ({
        ...item,
        dirtyFlag: 'D',
      }));

      return [...activeItems, ...deletedItemsData];
    },

    validateAllItems: async () => {
      try {
        const forms = [...itemFormRefs.current.values()].filter(Boolean);
        if (forms.length === 0) {
          return Promise.resolve();
        }
        await Promise.all(
          forms.map((form) => {
            if (!form || typeof form.validateFields !== 'function') {
              return Promise.resolve();
            }
            return form.validateFields();
          }),
        );
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }));

  useEffect(() => {
    if (filteredItems.length === 0 && !claimItemsQueryData) {
      return;
    }
    const deletedItemIDs = new Set(deletedItemsRef.current.map((item) => item.itemID).filter((id) => id && id !== 0));
    setItemList(
      filteredItems
        .filter((row) => {
          return !deletedItemIDs.has(row.itemID);
        })
        .map((row) => ({
          ...row,
          editMode: true,
          isNew: false,
          shipDate: `${row.shipDate}Z`,
        })),
    );
    setDirtyItemKeys(new Set());
  }, [filteredItems, claimItemsQueryData]);

  const { handleNextPage, scrollHasMore } = useOCClaimScrollPagination({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedCount: claimItems.data?.length ?? ZERO,
    totalRows: claimItems.totalRows ?? ZERO,
  });

  const createNewItem = () => ({
    billedAmount: 0,
    correctCharges: 0,
    dirtyFlag: 'i',
    editMode: true,
    fbOcKey: claimDetails.fbOcKey,
    invoiceNumber: undefined,
    isNew: true,
    itemID: 0,
    overchargeAmount: 0,
    packageNumber: undefined,
    paidAmount: 0,
    proNo: undefined,
    recoveryAmount: 0,
    shipDate: undefined,
    tempId: `tempId-${generateUUIDv4()}`,
    weight: 0,
  });

  const handleAddNew = (itemKey) => {
    setItemList((prev) => {
      const insertIndex = prev.findIndex((item) => getItemKey(item) === itemKey);
      if (insertIndex === -1) {
        return prev;
      }
      const newItem = createNewItem();
      const updated = [...prev];
      updated.splice(insertIndex + 1, 0, newItem);
      return updated;
    });
  };

  const handleSubmit = (_data, itemKey) => {
    const form = itemFormRefs.current.get(itemKey);
    if (form) {
      const item = itemList.find((i) => getItemKey(i) === itemKey);
      if (item && !item.isNew) {
        markItemDirty(itemKey);
      }
    }
  };

  const handleDelete = useCallback(
    (itemKey) => {
      if (itemList.length <= 1) {
        showNotification({
          message: 'At least one claim item is required. Cannot delete.',
          type: NOTIFICATION_TYPES.ERROR,
        });
      } else {
        const itemToDelete = itemList.find((item) => getItemKey(item) === itemKey);
        if (!itemToDelete) {
          return;
        }

        if (itemToDelete.isNew) {
          const remainingItems = itemList.filter((item) => getItemKey(item) !== itemKey);
          setItemList(remainingItems);
          itemFormRefs.current.delete(itemKey);
        } else {
          const remainingItems = itemList.filter((item) => getItemKey(item) !== itemKey);
          setItemList(remainingItems);
          itemFormRefs.current.delete(itemKey);
          setDeletedItems((prev) => [...prev, itemToDelete]);
        }
      }
    },
    [itemList],
  );

  const overChargePercent = Form.useWatch('overChargePercent', overchargePercentForm);

  const handleOverChargePercentCalculation = () => {
    if (!claimDetails?.fbOcKey) {
      return;
    }
    applyRecoveryPercent({
      fbOcKey: claimDetails.fbOcKey,
      percentage: overChargePercent,
    });
  };

  const handleUploadFile = async (data) => {
    try {
      const handleSuccess = () => {
        setUploadModal(true);
      };
      dispatch(setAppLoader(true));
      const file = await convertBlobToBase64(data[0]);
      dispatch(
        getOverChargeItemsFromExcelInProgress({
          fbocKey: claimDetails?.fbOcKey,
          file: file.split(',')[1],
          handleSuccess,
        }),
      );
    } catch {
      dispatch(setAppLoader(false));
      dispatch(
        setWarningModal({
          description: 'Invalid file, Please verify the selected file.',
          open: true,
        }),
      );
    }
  };

  const handleUploadFileError = (e) => {
    dispatch(setWarningModal({ description: e?.message, open: true }));
  };

  const handleUploadApply = (excelSheetItems) => {
    const items = excelSheetItems.map((row) => {
      const paidAmountL = row.paidAmount || 0;
      const correctChargesL = row.correctCharges || 0;
      let diffAmount = paidAmountL - correctChargesL;
      diffAmount = diffAmount >= 0 ? diffAmount : diffAmount * -1;
      return { ...row, overchargeAmount: diffAmount };
    });
    const itemListExcel = items.map((row) => ({ ...row, dirtyFlag: 'I' }));
    const claimDetailsLocal = cloneDeep(claimDetails);
    const formData = overChargeClaimsItemsSectionLocalToAPI(claimDetailsLocal);
    formData.claimItem = itemListExcel;
    formData.handleSuccess = () => {
      setUploadModal(false);
      invalidateOCClaimTabQueries(queryClient, claimDetails?.fbOcKey);
      queryClient.invalidateQueries({
        exact: false,
        queryKey: ['claims-over-charge-details', claimDetails?.fbOcKey],
      });
    };
    formData.uploadedFromExcel = true;
    dispatch(updateOverChargeClaimInProgress(formData));
  };

  const isItemsLoading = isLoading || (isFetching && !isFetchingNextPage);

  return (
    <div className="d-flex py-16 h-100" style={{ flexDirection: 'column' }}>
      <Row className="px-30 align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Over Charge Claims Items</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16} align="middle">
            <Col>
              <Files
                className="files-dropzone"
                onChange={handleUploadFile}
                onError={handleUploadFileError}
                accepts={['.xls', '.xlsx']}
                multiple={false}
                minFileSize={0}
                clickable
              >
                <Button>
                  <SvgConverter Icon={FileIcon} color="white" width={16} height={16} className="mr-5" />
                  Upload File
                </Button>
              </Files>
            </Col>
            <Col>
              <Button onClick={() => setEmailDetailsModal(true)}>View Email Status</Button>
            </Col>
            <Col>
              <Form form={overchargePercentForm}>
                <FormInput name="overChargePercent" inputType="number" editMode margin="m-0" placeholder=" " />
              </Form>
            </Col>
            <Col>
              <Button
                disabled={
                  claimDetails?.statusCode === 'C'
                    ? !overChargePercent || overChargePercent > OVERCHARGE_PERCENT_MAX
                    : true
                }
                onClick={handleOverChargePercentCalculation}
                loading={isApplyingRecovery}
              >
                Apply
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100" />
      {itemList.length > 0 || isItemsLoading ? (
        <ScrollPagination
          className="px-30 my-10 flex-1"
          loadMore={handleNextPage}
          isLoading={isFetchingNextPage}
          hasMore={scrollHasMore}
          scrollOffset={30}
          disabledForceNextPage
          isSkipScrollToBottom
        >
          {itemList.map((row) => {
            const itemKey = getItemKey(row);
            return (
              <OverChargeItemsCard
                handleAddNew={handleAddNew}
                noteDetails={row}
                key={itemKey}
                itemKey={itemKey}
                handleSubmit={handleSubmit}
                handleDelete={handleDelete}
                onFieldChange={() => markItemDirty(itemKey)}
                totalItemsCount={itemList.length}
                registerForm={registerItemForm}
              />
            );
          })}
        </ScrollPagination>
      ) : (
        <EmptyRecords />
      )}
      <div className="px-30">
        <Paper className="background-header px-16 py-5">
          <Row justify="space-around" className="px-10" gutter={10}>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={formateAmount(isClaimItemsForActiveClaim ? claimItems.totalBilledAmount : 0)}
                label="Billed Amount"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
            <Col>
              <VerticalLine />
            </Col>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={formateAmount(isClaimItemsForActiveClaim ? claimItems.totalPaidAmount : 0)}
                label="Paid Amount"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
            <Col>
              <VerticalLine />
            </Col>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={formateAmount(isClaimItemsForActiveClaim ? claimItems.totalCorrectCharges : 0)}
                label="Correct Charges"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
            <Col>
              <VerticalLine />
            </Col>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={formateAmount(isClaimItemsForActiveClaim ? claimItems.totalOverchargeAmount : 0)}
                label="Overcharge Sum"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
            <Col>
              <VerticalLine />
            </Col>
            <Col>
              <FooterContentCard
                reverse
                align="middle"
                count={formateAmount(isClaimItemsForActiveClaim ? claimItems.totalRecoveryAmount : 0)}
                label="Recovery Sum"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
          </Row>
        </Paper>
      </div>
      <OverChargeClaimEmailDetailsModal
        open={emailDetailsModal}
        setOpen={setEmailDetailsModal}
        fbocKey={claimDetails?.fbOcKey}
      />
      <OverChargeUploadItemsModal
        open={uploadModal}
        setOpen={setUploadModal}
        items={itemsFromExcel?.data || []}
        handleApply={handleUploadApply}
      />
      <AlertModal
        open={Boolean(duplicateClaimText)}
        description={duplicateClaimText}
        onOk={() => setDuplicateClaimText(false)}
      />
    </div>
  );
});

export default OverChargeItems;
